import { ButcherHeader } from '@components/ButcherHeader';
import { ContactSupportModalModal } from '@components/Modal/ContactSupportModal';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { useToast } from '@contexts/Toast';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import {
  AppSettingsSubscriptionSteps,
  errorMessage,
  OrderProcessingMasterData,
  searchConfig,
} from '@helpers/Constants';
import { SortIcon } from '@helpers/Constants/Icons';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { formatDateUSLong } from '@helpers/DateTime';
import { PaymentHistory, Subscription } from '@helpers/Models';
import {
  formatDateRange,
  handleDateFormat,
} from '@services/OrderCreationService';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const BillingDetails: React.FC = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();

  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[] | null>(
    null,
  );
  const [subscriptionDetails, setSubscriptionDetails] =
    useState<Subscription | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchKey, setSearchKey] = useState<string>('');
  const [debouncedSearchKey, setDebouncedSearchKey] = useState<string>('');
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [selectedSort, setSelectedSort] = useState<SortValue>('mostRecent');
  const [showContactForm, setShowContactForm] = useState<boolean>(false);

  const BreadCrumbsProps = {
    steps: AppSettingsSubscriptionSteps,
    activeItem: 'Billing Details',
    type: OrderProcessingMasterData.APP_SETTINGS,
  };

  const sortOptions = [
    { label: 'Most Recent', value: 'mostRecent' },
    { label: 'Highest Invoice Amount', value: 'highestAmount' },
    { label: 'Lowest Invoice Amount', value: 'lowestAmount' },
  ] as const;

  type SortValue = (typeof sortOptions)[number]['value'];

  /**
   * Debounces the search input.
   *
   * - Delays updating the debounced search key
   * - Prevents frequent API calls on every keystroke
   * - Clears the timeout on input change or unmount
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchKey(searchKey);
    }, searchConfig.SEARCH_DELAY);

    return () => {
      clearTimeout(handler);
    };
  }, [searchKey]);

  useEffect(() => {
    fetchSubscriptionDetails();
    fetchPaymentHistory(debouncedSearchKey);
  }, [debouncedSearchKey]);

  /**
   * Closes the sort dropdown when clicking outside of it.
   *
   * - Listens for global mouse down events
   * - Checks if the click occurred outside the dropdown element
   * - Closes the dropdown if clicked outside
   * - Cleans up the event listener on unmount
   */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(e.target as Node)
      ) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /**
   * Fetches the current subscription details from the backend.
   *
   * @async
   * @function fetchSubscriptionDetails
   * @returns {Promise<void>}
   *
   * @remarks
   * - Retrieves subscription data using the API endpoint.
   * - Updates the local state with the fetched subscription details.
   * - Stores the latest subscription status in `localStorage` for global access.
   * - Displays an error toast message if the request fails.
   */
  const fetchSubscriptionDetails = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await Axios.get(
        ApiEndpoints.FETCH_BUTCHER_SUBSCRIPTION_DETAILS,
      );
      setSubscriptionDetails(response.data.data.subscription);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetches payment history based on search and sort criteria.
   *
   * - Sends a request with search key and sort option
   * - Updates payment history state with the response data
   * - Uses default sort if none is provided
   * - Manages loading state during the API call
   * - Displays an error toast if the request fails
   *
   * @param {string} searchKey - Invoice ID or keyword to filter results
   * @param {SortValue} [sort] - Sort option (defaults to selectedSort)
   * @returns {Promise<void>} Resolves when the fetch operation completes
   */
  const fetchPaymentHistory = async (
    searchKey: string,
    sort: SortValue = selectedSort,
  ): Promise<void> => {
    try {
      setLoading(true);
      const response = await Axios.post(ApiEndpoints.FETCH_PAYMENT_HISTORY, {
        sort: sort,
        search: searchKey,
      });
      setPaymentHistory(response.data.data.paymentHistory);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles sort selection change for payment history.
   *
   * - Updates the selected sort option
   * - Closes the sort dropdown
   * - Fetches payment history based on the new sort and current search key
   *
   * @param {SortValue} sort - Selected sort option
   * @returns {void}
   */
  const handleSortChange = (sort: SortValue): void => {
    setSelectedSort(sort);
    setIsSortOpen(false);
    fetchPaymentHistory(searchKey, sort);
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      {/* Sidebar */}
      <SideNav active={10} />
      {/* Main Content */}
      <div
        className={`flex-1 p-1 overflow-auto relative ${loading ? 'opacity-50' : 'opacity-100'}`}
      >
        {showContactForm && (
          <ContactSupportModalModal onClose={() => setShowContactForm(false)} />
        )}
        {/* Header */}
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        <div className="flex items-center justify-between mt-4 mb-4">
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-[#1C1B1F] hover:text-primary text-lg mr-2 focus:outline-none"
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </button>
            <h2 className="text-2xl font-semibold ml-2">Billing Details</h2>
          </div>
        </div>
        {/* Main content */}
        <div className="p-2">
          {/* First Card - about plan */}
          <div className="border border-[#E8E8E8] p-3 rounded-md">
            <div className="flex flex-row justify-between">
              <span className="text-[#000000E5] text-[18px]">
                Current Billing Cycle
              </span>
              <div className="bg-[#0000001F] px-2 py-2 rounded-md">
                <span className="text-[#3F3F3F]">
                  From{' '}
                  {formatDateRange(
                    subscriptionDetails?.invoiceCycleStart ?? '',
                    subscriptionDetails?.nextBillingDate ?? '',
                  )}
                </span>
              </div>
            </div>
            {/* Plan Name */}
            <div>
              <span className="text-[28px] font-semibold text-[#3F3F3F]">
                {subscriptionDetails?.subscriptionPlan}
              </span>
            </div>

            <div className="flex flex-row mt-3">
              <div className="">
                <span className="uppercase text-[#3F3F3F] text-[16px]">
                  ANIMALS PROCESSED:{' '}
                </span>
                <span className="font-bold text-[#3F3F3F] text-xl">
                  {' '}
                  {subscriptionDetails?.orderSpeciesCount ?? ''}
                </span>
                <span className="ml-2 text-sm font-medium">
                  This billing cycle
                </span>
              </div>
              <span className="text-[#B0B0B0] ml-3 mr-3 mt-1">|</span>
              <div className="">
                <span className="uppercase text-[#3F3F3F] text-[16px]">
                  PAYMENT DUE:{' '}
                </span>
                <span className="font-bold text-[#3F3F3F] text-2xl">
                  {' '}
                  $
                  {subscriptionDetails?.estimatedCostWithDiscount?.toFixed(2) ??
                    '0.00'}
                </span>
                <span className="ml-2 text-sm font-medium">
                  Due {handleDateFormat(subscriptionDetails?.nextBillingDate)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="border border-[#E8E8E8] p-3 rounded-md mt-5">
            <div className="flex flex-row gap-3">
              <span className="p-2 bg-[#FEF3C7] rounded-md">
                <svg
                  width="20"
                  height="16"
                  viewBox="0 0 20 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 2V14C20 14.55 19.8042 15.0208 19.4125 15.4125C19.0208 15.8042 18.55 16 18 16H2C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2ZM2 4H18V2H2V4ZM2 8V14H18V8H2Z"
                    fill="#F59E0B"
                  />
                </svg>
              </span>
              <span className="font-medium text-[#374151]">Payment Method</span>
              <span className="font-bold">
                •••• {subscriptionDetails?.paymentMethod ?? ''}
              </span>
              <span className="text-[#6B7280]">
                {subscriptionDetails?.paymentBrand ?? ''} ending in{' '}
                {subscriptionDetails?.paymentMethod ?? ''}
              </span>
            </div>
          </div>

          {/* Utilized Cost for This Month */}
          <div className="border border-[#E8E8E8] p-3 rounded-md mt-5">
            <div className="flex flex-col">
              <span className="text-[20px] font-bold">
                Utilized Cost for This Month
              </span>
              {/* Today's Date here */}
              <span className="text-[#6C757D]">
                As of {formatDateUSLong(new Date())}
              </span>
            </div>

            <div className="grid grid-cols-2 justify-between mt-5">
              <div className="flex flex-col">
                <span className="font-medium text-[#1A1A1A]">
                  Subscription Fee
                </span>
                <span className="text-[#6C757D]">
                  {subscriptionDetails?.orderSpeciesCount ?? 0} animals × $
                  {subscriptionDetails?.price} per animal
                </span>
              </div>
              <div className="text-end text-[#1A1A1A] font-bold text-lg">
                ${subscriptionDetails?.estimatedCost.toFixed(2) ?? '0.00'}
              </div>
            </div>
            <div className="grid grid-cols-2 justify-between mt-5">
              {subscriptionDetails?.promoCodes
                ?.filter((promo) => promo.isActive === 1)
                .map((promo) => (
                  <>
                    <div className="flex flex-col">
                      <span className="font-medium text-[#1A1A1A]">
                        Discount Applied
                      </span>
                      <span className="text-[#6C757D]">
                        {promo.PromoCodes.description}
                      </span>
                    </div>
                    <div className="text-end text-[#10B981] font-bold text-lg">
                      -$
                      {subscriptionDetails?.discountedPrice.toFixed(2) ??
                        '0.00'}
                    </div>
                  </>
                ))}
            </div>

            <div className="flex justify-between bg-[#F8F8F8] rounded-md px-3 py-5 mb-4 mt-4">
              <span className="text-[#1A1A1A] font-bold text-[18px]">
                Estimated Total
              </span>
              <span className="text-[#004834] font-bold text-2xl">
                $
                {subscriptionDetails?.estimatedCostWithDiscount.toFixed(2) ??
                  '0.00'}
              </span>
            </div>
          </div>
          {/* Payment History */}
          <div className="mt-4 p-3">
            <div className="flex flex-row items-center justify-between">
              <span className="font-bold text-[20px]">Payment History</span>
              <div className="flex items-center gap-2">
                {/* Search input */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchKey}
                    onChange={(e) => setSearchKey(e.target.value)}
                    className="border border-gray-200 rounded-md py-2 px-3 text-sm focus:outline-none w-40 pr-8"
                  />
                  <svg
                    className="absolute right-2 top-2 w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>

                {/* Sort icon */}
                <div className="relative" ref={sortDropdownRef}>
                  <span
                    onClick={() => setIsSortOpen((prev) => !prev)}
                    className="p-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-md cursor-pointer hover:bg-gray-100 flex items-center gap-1.5"
                  >
                    <SortIcon />
                  </span>

                  {isSortOpen && (
                    <ul className="absolute right-0 top-full mt-1 rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.10)] bg-white py-1 z-[1000] w-max min-w-[200px]">
                      {sortOptions.map((option) => (
                        <li
                          key={option.value}
                          onClick={() => handleSortChange(option.value)}
                          className={`px-5 py-3 text-sm cursor-pointer transition-colors ${selectedSort === option.value
                              ? 'font-semibold'
                              : 'text-[#000A16] hover:bg-[#F2F2F5]'
                            }`}
                        >
                          {option.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="relative mt-4">
              <div className="overflow-y-auto h-[calc(100vh-450px)]">
                <table className="w-full table-auto border border-[#E8E8E8] rounded-lg">
                  <thead className="bg-[#F8F9FA] sticky top-0 z-20">
                    <tr className="text-[#6C757D] text-sm">
                      <th className="px-4 py-2 text-start font-semibold whitespace-nowrap">
                        INVOICE ID{' '}
                      </th>
                      <th className="px-4 py-2">
                        <div className="flex items-center gap-3 font-semibold">
                          <span>INVOICE CYCLE</span>
                        </div>
                      </th>
                      <th className="px-4 py-2 text-center font-semibold">
                        BILLING DATE
                      </th>
                      <th className="px-4 py-2 text-center font-semibold">
                        AMOUNT
                      </th>
                      <th className="px-4 py-2 text-center font-semibold">
                        STATUS
                      </th>
                      <th className="px-4 py-2 text-start font-semibold">
                        ACTIONS
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {paymentHistory?.map((data, index) => (
                      <tr key={index} className="mt-3">
                        {/* bg-tertiary border border-primary bg-[#FFFEFA] */}
                        <td className=" px-4 py-2 text-start">
                          {data.invoiceId}
                        </td>
                        <td className="px-4 py-2">
                          {formatDateRange(
                            data.invoiceCycleStart,
                            data.invoiceCycleEnd,
                          )}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {handleDateFormat(
                            data.stripeInvoiceDetail.billingDate,
                          )}
                        </td>
                        {/* Not equal to 1 is Unpaid */}
                        <td
                          className={`px-4 py-2 text-center ${data.status != 1 ? 'text-[#DC2626]' : 'text-[#848484]'}`}
                        >
                          ${data.amount}
                        </td>

                        <td className="px-4 py-2 text-center">
                          <div className="inline-block mb-1 ml-2">
                            <div
                              className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full w-fit
                             ${data.status != 1
                                  ? 'bg-[#F8D4D4]'
                                  : 'bg-[#CFF1E6]'
                                }`}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d={
                                    data.status != 1
                                      ? 'M4.48 10.5L7 7.98L9.52 10.5L10.5 9.52L7.98 7L10.5 4.48L9.52 3.5L7 6.02L4.48 3.5L3.5 4.48L6.02 7L3.5 9.52L4.48 10.5ZM7 14C6.03167 14 5.12167 13.8162 4.27 13.4488C3.41833 13.0813 2.6775 12.5825 2.0475 11.9525C1.4175 11.3225 0.91875 10.5817 0.55125 9.73C0.18375 8.87833 0 7.96833 0 7C0 6.03167 0.18375 5.12167 0.55125 4.27C0.91875 3.41833 1.4175 2.6775 2.0475 2.0475C2.6775 1.4175 3.41833 0.91875 4.27 0.55125C5.12167 0.18375 6.03167 0 7 0C7.96833 0 8.87833 0.18375 9.73 0.55125C10.5817 0.91875 11.3225 1.4175 11.9525 2.0475C12.5825 2.6775 13.0813 3.41833 13.4488 4.27C13.8162 5.12167 14 6.03167 14 7C14 7.96833 13.8162 8.87833 13.4488 9.73C13.0813 10.5817 12.5825 11.3225 11.9525 11.9525C11.3225 12.5825 10.5817 13.0813 9.73 13.4488C8.87833 13.8162 7.96833 14 7 14ZM7 12.6C8.56333 12.6 9.8875 12.0575 10.9725 10.9725C12.0575 9.8875 12.6 8.56333 12.6 7C12.6 5.43667 12.0575 4.1125 10.9725 3.0275C9.8875 1.9425 8.56333 1.4 7 1.4C5.43667 1.4 4.1125 1.9425 3.0275 3.0275C1.9425 4.1125 1.4 5.43667 1.4 7C1.4 8.56333 1.9425 9.8875 3.0275 10.9725C4.1125 12.0575 5.43667 12.6 7 12.6Z'
                                      : 'M6.02 10.22L10.955 5.285L9.975 4.305L6.02 8.26L4.025 6.265L3.045 7.245L6.02 10.22ZM7 14C6.03167 14 5.12167 13.8162 4.27 13.4488C3.41833 13.0813 2.6775 12.5825 2.0475 11.9525C1.4175 11.3225 0.91875 10.5817 0.55125 9.73C0.18375 8.87833 0 7.96833 0 7C0 6.03167 0.18375 5.12167 0.55125 4.27C0.91875 3.41833 1.4175 2.6775 2.0475 2.0475C2.6775 1.4175 3.41833 0.91875 4.27 0.55125C5.12167 0.18375 6.03167 0 7 0C7.96833 0 8.87833 0.18375 9.73 0.55125C10.5817 0.91875 11.3225 1.4175 11.9525 2.0475C12.5825 2.6775 13.0813 3.41833 13.4488 4.27C13.8162 5.12167 14 6.03167 14 7C14 7.96833 13.8162 8.87833 13.4488 9.73C13.0813 10.5817 12.5825 11.3225 11.9525 11.9525C11.3225 12.5825 10.5817 13.0813 9.73 13.4488C8.87833 13.8162 7.96833 14 7 14ZM7 12.6C8.56333 12.6 9.8875 12.0575 10.9725 10.9725C12.0575 9.8875 12.6 8.56333 12.6 7C12.6 5.43667 12.0575 4.1125 10.9725 3.0275C9.8875 1.9425 8.56333 1.4 7 1.4C5.43667 1.4 4.1125 1.9425 3.0275 3.0275C1.9425 4.1125 1.4 5.43667 1.4 7C1.4 8.56333 1.9425 9.8875 3.0275 10.9725C4.1125 12.0575 5.43667 12.6 7 12.6Z'
                                  }
                                  fill={
                                    data.status != 1 ? '#DC2626' : '#009362'
                                  }
                                />
                              </svg>

                              <span
                                className={
                                  data.status != 1
                                    ? 'text-[#DC2626]'
                                    : 'text-[#009362]'
                                }
                              >
                                {data.status != 1 ? 'Unpaid' : 'Paid'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <div className="flex items-center gap-2">
                            {/* View button */}
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  '/subscription/details/billing/invoice',
                                  {
                                    state: { invoiceNumber: data.invoiceId },
                                  },
                                )
                              }
                              className="flex items-center gap-1 text-primary"
                            >
                              <span>View</span>
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M2 18C1.45 18 0.979167 17.8042 0.5875 17.4125C0.195833 17.0208 0 16.55 0 16V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H9V2H2V16H16V9H18V16C18 16.55 17.8042 17.0208 17.4125 17.4125C17.0208 17.8042 16.55 18 16 18H2ZM6.7 12.7L5.3 11.3L14.6 2H11V0H18V7H16V3.4L6.7 12.7Z"
                                  fill="#004834"
                                />
                              </svg>
                            </button>

                            {/* Pay button */}
                            {/* Temporarily disabling the Pay action until requirements are clarified */}
                            {/* <button
                              type="button"
                              onClick={() =>
                                navigate('/subscription/details/billing/invoice')
                              }
                              className="flex items-center gap-1 bg-primary text-white px-3 py-1.5 rounded-md"
                            >
                              <svg
                                width="17"
                                height="14"
                                viewBox="0 0 17 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M16.6667 1.66667V11.6667C16.6667 12.125 16.5035 12.5174 16.1771 12.8437C15.8507 13.1701 15.4583 13.3333 15 13.3333H1.66667C1.20833 13.3333 0.815972 13.1701 0.489583 12.8437C0.163194 12.5174 0 12.125 0 11.6667V1.66667C0 1.20833 0.163194 0.815972 0.489583 0.489583C0.815972 0.163194 1.20833 0 1.66667 0H15C15.4583 0 15.8507 0.163194 16.1771 0.489583C16.5035 0.815972 16.6667 1.20833 16.6667 1.66667ZM1.66667 3.33333H15V1.66667H1.66667V3.33333ZM1.66667 6.66667V11.6667H15V6.66667H1.66667Z"
                                  fill="white"
                                />
                              </svg>
                              <span>Pay</span>
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Need help */}
            <div className="mt-auto w-full flex items-center justify-between px-6 py-4 bg-white border border-gray-200 shadow-md rounded-xl">
              <div className="flex items-center gap-4">
                {/* Help icon */}
                <div className="flex-shrink-0">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.76 30.74C7.81333 29.9 6.12 28.76 4.68 27.32C3.24 25.88 2.1 24.1867 1.26 22.24C0.42 20.2933 0 18.2133 0 16C0 13.7867 0.42 11.7067 1.26 9.76C2.1 7.81333 3.24 6.12 4.68 4.68C6.12 3.24 7.81333 2.1 9.76 1.26C11.7067 0.42 13.7867 0 16 0C18.2133 0 20.2933 0.42 22.24 1.26C24.1867 2.1 25.88 3.24 27.32 4.68C28.76 6.12 29.9 7.81333 30.74 9.76C31.58 11.7067 32 13.7867 32 16C32 18.2133 31.58 20.2933 30.74 22.24C29.9 24.1867 28.76 25.88 27.32 27.32C25.88 28.76 24.1867 29.9 22.24 30.74C20.2933 31.58 18.2133 32 16 32C13.7867 32 11.7067 31.58 9.76 30.74ZM11.36 27.92L13.28 23.52C12.16 23.12 11.1933 22.5 10.38 21.66C9.56667 20.82 8.93333 19.84 8.48 18.72L4.08 20.56C4.69333 22.2667 5.64 23.76 6.92 25.04C8.2 26.32 9.68 27.28 11.36 27.92ZM8.48 13.28C8.93333 12.16 9.56667 11.18 10.38 10.34C11.1933 9.5 12.16 8.88 13.28 8.48L11.44 4.08C9.73333 4.72 8.24 5.68 6.96 6.96C5.68 8.24 4.72 9.73333 4.08 11.44L8.48 13.28ZM19.4 19.4C20.3333 18.4667 20.8 17.3333 20.8 16C20.8 14.6667 20.3333 13.5333 19.4 12.6C18.4667 11.6667 17.3333 11.2 16 11.2C14.6667 11.2 13.5333 11.6667 12.6 12.6C11.6667 13.5333 11.2 14.6667 11.2 16C11.2 17.3333 11.6667 18.4667 12.6 19.4C13.5333 20.3333 14.6667 20.8 16 20.8C17.3333 20.8 18.4667 20.3333 19.4 19.4ZM20.64 27.92C22.32 27.28 23.7933 26.3267 25.06 25.06C26.3267 23.7933 27.28 22.32 27.92 20.64L23.52 18.72C23.12 19.84 22.5067 20.8067 21.68 21.62C20.8533 22.4333 19.8933 23.0667 18.8 23.52L20.64 27.92ZM23.52 13.2L27.92 11.36C27.28 9.68 26.3267 8.20667 25.06 6.94C23.7933 5.67333 22.32 4.72 20.64 4.08L18.8 8.56C19.8933 8.96 20.84 9.56667 21.64 10.38C22.44 11.1933 23.0667 12.1333 23.52 13.2Z"
                      fill="#004834"
                    />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-[#1A1A1A]">
                    Need Help with Billing?
                  </span>
                  <span className="text-sm text-[#6C757D]">
                    Our support team is here to assist you with any billing
                    questions or concerns
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowContactForm(true)}
                className="flex-shrink-0 flex items-center gap-2 bg-primary text-white text-sm font-semibold px-5 py-2.5 rounded-md"
              >
                {/* Mail icon */}
                <svg
                  width="20"
                  height="16"
                  viewBox="0 0 20 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2V14C20 14.55 19.8042 15.0208 19.4125 15.4125C19.0208 15.8042 18.55 16 18 16H2ZM10 9L2 4V14H18V4L10 9ZM10 7L18 2H2L10 7ZM2 4V2V14V4Z"
                    fill="white"
                  />
                </svg>
                <span className="font-medium">Contact Support</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
